import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, CheckCircle, Clock, Users, ArrowRight, FileText, Search, Shield, RefreshCw, Scale, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';

export const AdHocTrainingPage = () => {
  const { language } = useLanguage();

  const useCases = language === 'fr' ? [
    { icon: FileText, name: 'Drafting & rédaction', desc: 'Génération de premières versions de contrats, clauses types, courriers, conclusions. Gain de temps de 50% sur les premières ébauches.' },
    { icon: Search, name: 'Relecture & comparaison de contrats', desc: 'Identification automatique d\'écarts entre versions, détection de clauses inhabituelles, comparaison de contrats avec un modèle de référence.' },
    { icon: Shield, name: 'Analyse de niveau de risque', desc: 'Scoring automatisé du niveau de risque contractuel, alertes sur clauses sensibles, rapports de risque synthétiques.' },
    { icon: RefreshCw, name: 'Renouvellements de contrats', desc: 'Analyse automatisée des échéances de renouvellement, alertes proactives, synthèse des obligations et conditions.' },
    { icon: Scale, name: 'Recherche juridique augmentée', desc: 'Recherche jurisprudentielle assistée par IA, synthèse de doctrine, veille automatisée sur vos matières.' },
    { icon: Layers, name: 'Due diligence automatisée', desc: 'Extraction automatisée d\'informations clés dans les datarooms, détection de red flags, rapports de synthèse.' },
  ] : [
    { icon: FileText, name: 'Drafting & writing', desc: 'First-draft generation for contracts, standard clauses, letters, briefs. 50% time savings on first drafts.' },
    { icon: Search, name: 'Contract review & comparison', desc: 'Automatic identification of discrepancies between versions, unusual clause detection, comparison with reference templates.' },
    { icon: Shield, name: 'Risk level analysis', desc: 'Automated contractual risk scoring, alerts on sensitive clauses, synthetic risk reports.' },
    { icon: RefreshCw, name: 'Contract renewals', desc: 'Automated renewal deadline analysis, proactive alerts, obligation and condition summaries.' },
    { icon: Scale, name: 'Augmented legal research', desc: 'AI-assisted case law research, doctrine synthesis, automated monitoring on your practice areas.' },
    { icon: Layers, name: 'Automated due diligence', desc: 'Automated extraction of key information from datarooms, red flag detection, summary reports.' },
  ];

  const results = language === 'fr' ? [
    'Adoption des outils IA par 85% des équipes formées',
    'ROI positif constaté dès les 3 premiers mois',
    'Réduction de 30% du temps de relecture contractuelle',
    'Satisfaction client de 9.2/10 en moyenne',
  ] : [
    '85% AI tool adoption rate among trained teams',
    'Positive ROI observed within the first 3 months',
    '30% reduction in contract review time',
    '9.2/10 average client satisfaction',
  ];

  const faqs = language === 'fr' ? [
    { q: "Comment se déroule une formation ad hoc ?", a: "Nous réalisons d'abord un audit de vos besoins et workflows, puis concevons un programme sur-mesure avec des cas d'usage issus de vos propres documents. La formation peut être en webinaire ou présentiel." },
    { q: "Combien de temps dure la formation ?", a: "De 1 jour (atelier intensif) à 3 mois (programme complet avec suivi). Nous nous adaptons au planning de vos équipes." },
    { q: "Quels outils IA sont couverts ?", a: "Nous sélectionnons les outils les plus adaptés à vos besoins et à votre pratique, qu'il s'agisse de solutions spécialisées en recherche juridique, en drafting ou en analyse documentaire. Nous recommandons une approche multi-outils." },
    { q: "Y a-t-il un suivi post-formation ?", a: "Oui, toutes nos formations ad hoc incluent un suivi de 30 jours pour répondre aux questions et accompagner l'adoption." },
  ] : [
    { q: "How does custom training work?", a: "We first audit your needs and workflows, then design a tailored program with use cases from your own documents. Training can be via webinar or on-site." },
    { q: "How long does the training last?", a: "From 1 day (intensive workshop) to 3 months (comprehensive program with follow-up). We adapt to your team's schedule." },
    { q: "Which AI tools are covered?", a: "We select the tools best suited to your needs and practice, whether specialized in legal research, drafting, or document analysis. We recommend a multi-tool approach." },
    { q: "Is there post-training support?", a: "Yes, all our custom training programs include 30-day follow-up support to answer questions and support adoption." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={language === 'fr' ? "Formation IA sur-mesure cabinets d'avocats | Gaius" : "Custom AI Training for Law Firms | Gaius"}
        description={language === 'fr' ? "Formation IA ad hoc pour cabinets d'avocats et directions juridiques. Drafting, relecture, comparaison de contrats, analyse de risques. Webinaire ou présentiel." : "Custom AI training for law firms and legal departments. Drafting, review, contract comparison, risk analysis. Webinar or on-site."}
        keywords={language === 'fr' ? "formation ia cabinet avocat, formation ia direction juridique, drafting ia, relecture contrat ia, formation sur mesure avocat" : "custom ai training law firm, legal department ai training, ai drafting, contract review ai, bespoke lawyer training"}
        canonicalUrl={language === 'fr' ? "https://gaius-tech.com/formation-ia-sur-mesure-cabinet-avocat" : "https://gaius-tech.com/custom-ai-training-law-firm"}
        alternateUrlFr="https://gaius-tech.com/formation-ia-sur-mesure-cabinet-avocat"
        alternateUrlEn="https://gaius-tech.com/custom-ai-training-law-firm"
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {language === 'fr' ? 'Formation IA sur-mesure pour votre cabinet' : 'Custom AI training for your firm'}
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
            {language === 'fr'
              ? 'Programmes adaptés à vos documents, vos workflows et vos matières. Formation pratique sur les cas d\'usage les plus impactants pour votre organisation.'
              : 'Programs tailored to your documents, workflows, and practice areas. Hands-on training on the most impactful use cases for your organization.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg">
              <Link to="/contact">{language === 'fr' ? 'Demander un devis' : 'Request a quote'}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-4 text-center">
            {language === 'fr' ? 'Cas d\'usage couverts' : 'Use cases covered'}
          </h2>
          <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Nos formations s\'appuient sur vos propres documents et workflows pour un impact immédiat.'
              : 'Our training uses your own documents and workflows for immediate impact.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((uc, i) => (
              <div key={i} className="p-6 rounded-xl border border-border bg-card hover:shadow-md hover:border-primary/20 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <uc.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground">{uc.name}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
            {language === 'fr' ? 'Résultats constatés' : 'Proven results'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {results.map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-foreground">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
            {language === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions'}
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-card rounded-xl border border-border px-6">
                <AccordionTrigger className="text-left text-base font-medium text-foreground hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-primary-foreground mb-4">
            {language === 'fr' ? 'Formez vos équipes à l\'IA' : 'Train your teams on AI'}
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8">
            {language === 'fr' ? 'Devis personnalisé. Suivi post-formation de 30 jours inclus.' : 'Custom quote. 30-day post-training support included.'}
          </p>
          <Button asChild size="lg" variant="secondary">
            <Link to="/contact" className="flex items-center">
              {language === 'fr' ? 'Demander un devis' : 'Request a quote'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
